// src/pages/ErrorPage.js
// Full-page outage states. Shown two ways:
//   - in place of the routes when a page-loading API call fails (api.js fires
//     'api:error', App.js swaps this in), with onRetry reloading the page;
//   - at /error?code=… for redirects, e.g. the backend's Google sign-in callback.
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Mascot from '../components/Mascot';
import '../styles/components/StatePage.css';

const ERRORS = {
  backend_down: {
    display: 'Out of reach.',
    code: 'ERROR · SERVICE UNREACHABLE',
    say: 'I can’t reach our servers. Your guides are safe.',
    title: 'Michii is temporarily unavailable',
    desc: 'Our servers aren’t answering. This is usually brief, so try again in a minute.',
  },
  offline: {
    display: 'No signal.',
    code: 'ERROR · YOU’RE OFFLINE',
    say: 'Your browser says there’s no internet connection.',
    title: 'You’re offline',
    desc: 'Check your Wi-Fi or network cable, then try again.',
  },
  db_down: {
    display: 'Hold on.',
    code: 'ERROR 503 · STORAGE UNAVAILABLE',
    say: 'Nothing you saved is lost. I just can’t open it right now.',
    title: 'We can’t load your guides right now',
    desc: 'Our database isn’t responding. We’re on it; try again in a few minutes.',
  },
  google_auth: {
    display: 'Sign-in stalled.',
    code: 'ERROR · GOOGLE SIGN-IN FAILED',
    say: 'Google didn’t finish signing you in.',
    title: 'Google sign-in didn’t go through',
    desc: 'The sign-in was cancelled or Google didn’t respond. Try again, or sign in with your email and password.',
    retry: { to: '/login', label: 'Back to sign in' },
  },
  server: {
    display: 'Hold on.',
    code: 'ERROR 500 · SOMETHING BROKE ON OUR SIDE',
    say: 'That one’s on us, not you.',
    title: 'Something went wrong',
    desc: 'The server hit an error loading this page. Try again; if it keeps happening, tell us what you were doing.',
  },
};

export default function ErrorPage({ code, onRetry }) {
  const [params] = useSearchParams();
  const e = ERRORS[code || params.get('code')] || ERRORS.server;
  const retry = e.retry || { to: '/', label: 'Go home' };

  return (
    <div className="state-page" role="alert">
      <div className="state-page__inner">
        <div>
          <p className="state-page__display">{e.display}</p>
          <p className="state-page__code">{e.code}</p>
        </div>
        <div className="state-page__side">
          <div className="state-say">
            <Mascot size={40} />
            <div className="state-say__bubble">
              <b>Michii says</b>
              <span>{e.say}</span>
            </div>
          </div>
          <h1 className="state-page__title">{e.title}</h1>
          <p className="state-page__desc">{e.desc}</p>
          <div className="state-page__actions">
            {onRetry
              ? <button className="lp-btn" onClick={onRetry}>Try again</button>
              : <Link to={retry.to} className="lp-btn">{retry.label}</Link>}
            <Link to="/contact" className="lp-tlink">Contact support</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
